// src/pages/SponsorsPage.js
import React, { useState, useEffect } from 'react';
import { Mail, Globe, ArrowRight, CheckCircle, AlertCircle } from 'lucide-react';
import sanityClient from '../lib/sanityClient';

function SponsorsPage() {
  const [sponsors, setSponsors] = useState({
    platinum: [],
    gold: [],
    silver: [],
    bronze: [],
    supporter: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Contact form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    phone: '',
    message: '',
    sponsorshipType: 'Interested in learning more',
  });
  const [formStatus, setFormStatus] = useState({
    submitted: false,
    success: false,
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    sanityClient
      .fetch(
        `*[_type == "sponsor" && isActive == true] | order(tier asc, order asc) {
          _id,
          name,
          "logoUrl": logo.asset->url,
          website,
          tier,
          description,
          contactName,
          contactEmail,
          slug
        }`
      )
      .then((data) => {
        // Group sponsors by tier
        const groupedSponsors = {
          platinum: [],
          gold: [],
          silver: [],
          bronze: [],
          supporter: [],
        };

        data.forEach((sponsor) => {
          if (groupedSponsors[sponsor.tier]) {
            groupedSponsors[sponsor.tier].push(sponsor);
          }
        });

        setSponsors(groupedSponsors);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching sponsors:', err);
        setError('Failed to load sponsors. Please try again later.');
        setIsLoading(false);
      });
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Here we'd typically use a service like EmailJS, Formspree, or a custom endpoint
      // For this example, we'll simulate an API call

      // In a real implementation, you'd replace this with an actual API call:
      // const response = await fetch('https://your-api-endpoint.com/send-email', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(formData)
      // });

      // For demo purposes, simulate a successful submission
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Simulated success response
      setFormStatus({
        submitted: true,
        success: true,
        message:
          'Thank you for your interest! We will contact you shortly to discuss sponsorship opportunities.',
      });

      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        company: '',
        phone: '',
        message: '',
        sponsorshipType: 'Interested in learning more',
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      setFormStatus({
        submitted: true,
        success: false,
        message:
          'There was an error submitting your request. Please try again or contact us directly.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-black min-h-screen flex justify-center items-center">
        <div className="text-white text-xl">Loading sponsors...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-black min-h-screen flex justify-center items-center">
        <div className="bg-gray-900 p-6 rounded-lg text-white text-xl">{error}</div>
      </div>
    );
  }

  const renderSponsorTier = (tier, title) => {
    if (sponsors[tier].length === 0) return null;

    return (
      <div className="mb-16">
        <h2 className="text-2xl font-bold text-white mb-6">{title} Sponsors</h2>

        <div
          className={`grid ${tier === 'platinum' ? 'grid-cols-1' : tier === 'gold' ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 md:grid-cols-3'} gap-8`}
        >
          {sponsors[tier].map((sponsor) => (
            <div
              key={sponsor._id}
              className={`bg-gray-900 rounded-lg overflow-hidden border border-gray-800 hover:border-yellow-600 transition-all ${tier === 'platinum' ? 'p-8' : 'p-6'}`}
            >
              <div
                className={`flex ${tier === 'platinum' ? 'flex-col md:flex-row items-center md:items-start gap-8' : 'flex-col'}`}
              >
                <div
                  className={`${tier === 'platinum' ? 'w-64 h-40' : tier === 'gold' ? 'h-32' : 'h-24'} bg-white rounded-md flex items-center justify-center p-4`}
                >
                  <img
                    src={sponsor.logoUrl || '/sponsor-placeholder.png'}
                    alt={sponsor.name}
                    className="max-w-full max-h-full object-contain"
                    onError={(e) => {
                      e.currentTarget.src = '/sponsor-placeholder.png';
                    }}
                  />
                </div>
                <div className={`${tier === 'platinum' ? 'md:flex-1' : 'mt-4'}`}>
                  <h3
                    className={`font-bold text-white ${tier === 'platinum' ? 'text-2xl' : 'text-xl'} mb-2`}
                  >
                    {sponsor.name}
                  </h3>

                  {sponsor.description && (
                    <p className="text-gray-400 mb-4">{sponsor.description}</p>
                  )}

                  <div className="flex flex-wrap gap-3 mt-4">
                    {sponsor.website && (
                      <a
                        href={sponsor.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center text-yellow-500 hover:text-yellow-400"
                      >
                        <Globe className="h-4 w-4 mr-1" />
                        Website
                      </a>
                    )}
                    {sponsor.contactEmail && (
                      <a
                        href={`mailto:${sponsor.contactEmail}`}
                        className="flex items-center text-yellow-500 hover:text-yellow-400"
                      >
                        <Mail className="h-4 w-4 mr-1" />
                        Contact
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-black min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">Our Sponsors</h1>
        <p className="text-gray-400 text-lg mb-12">
          We are grateful to the following businesses and organizations for their support of
          Bombardiers Hockey.
        </p>

        {/* Render each tier of sponsors */}
        {renderSponsorTier('platinum', 'Platinum')}
        {renderSponsorTier('gold', 'Gold')}
        {renderSponsorTier('silver', 'Silver')}
        {renderSponsorTier('bronze', 'Bronze')}
        {renderSponsorTier('supporter', 'Team')}

        {/* Become a Sponsor Section */}
        <div className="mt-20 mb-16">
          <div className="bg-gray-900 rounded-lg overflow-hidden">
            <div className="p-8 md:p-12">
              <div className="md:flex">
                <div className="md:w-1/2 md:pr-12">
                  <h2 className="text-2xl font-bold text-white mb-4">Become a Sponsor</h2>
                  <p className="text-gray-400 mb-6">
                    Support youth hockey in our community by becoming a sponsor of the Bombardiers
                    Hockey team. Your contribution helps provide quality coaching, equipment, and
                    opportunities for young players.
                  </p>

                  <div className="bg-gray-800 rounded-lg p-6 mb-6">
                    <h3 className="text-lg font-semibold text-white mb-3">Sponsorship Benefits</h3>
                    <ul className="space-y-2 text-gray-300">
                      <li className="flex items-start">
                        <ArrowRight className="h-5 w-5 text-yellow-500 flex-shrink-0 mr-2" />
                        <span>Logo placement on team website and promotional materials</span>
                      </li>
                      <li className="flex items-start">
                        <ArrowRight className="h-5 w-5 text-yellow-500 flex-shrink-0 mr-2" />
                        <span>Recognition during home games and tournaments</span>
                      </li>
                      <li className="flex items-start">
                        <ArrowRight className="h-5 w-5 text-yellow-500 flex-shrink-0 mr-2" />
                        <span>Social media mentions and community engagement</span>
                      </li>
                      <li className="flex items-start">
                        <ArrowRight className="h-5 w-5 text-yellow-500 flex-shrink-0 mr-2" />
                        <span>Exclusive sponsorship opportunities for team events</span>
                      </li>
                    </ul>
                  </div>

                  <p className="text-gray-400">
                    Fill out the form to learn more about our sponsorship opportunities and how your
                    business can benefit from supporting youth hockey.
                  </p>
                </div>

                <div className="md:w-1/2 mt-8 md:mt-0">
                  {formStatus.submitted ? (
                    <div
                      className={`rounded-lg p-8 mb-6 ${formStatus.success ? 'bg-green-900' : 'bg-orange-400'}`}
                    >
                      <div className="flex items-center mb-4">
                        {formStatus.success ? (
                          <CheckCircle className="h-8 w-8 text-green-400 mr-3" />
                        ) : (
                          <AlertCircle className="h-8 w-8 text-red-400 mr-3" />
                        )}
                        <h3 className="text-xl font-bold text-white">
                          {formStatus.success ? 'Thank You!' : 'Submission Error'}
                        </h3>
                      </div>
                      <p className="text-white">{formStatus.message}</p>
                      {!formStatus.success && (
                        <button
                          type="button"
                          onClick={() =>
                            setFormStatus({ submitted: false, success: false, message: '' })
                          }
                          className="mt-4 bg-orange-400 hover:bg-orange-700 text-white px-4 py-2 rounded-md"
                        >
                          Try Again
                        </button>
                      )}
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="bg-gray-800 rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-white mb-4">Sponsorship Inquiry</h3>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label htmlFor="firstName" className="block text-gray-400 text-sm mb-1">
                            First Name *
                          </label>
                          <input
                            type="text"
                            id="firstName"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            required
                            className="bg-gray-700 text-white rounded-md px-4 py-2 w-full"
                          />
                        </div>
                        <div>
                          <label htmlFor="lastName" className="block text-gray-400 text-sm mb-1">
                            Last Name *
                          </label>
                          <input
                            type="text"
                            id="lastName"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            required
                            className="bg-gray-700 text-white rounded-md px-4 py-2 w-full"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label htmlFor="email" className="block text-gray-400 text-sm mb-1">
                            Email *
                          </label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                            className="bg-gray-700 text-white rounded-md px-4 py-2 w-full"
                          />
                        </div>
                        <div>
                          <label htmlFor="phone" className="block text-gray-400 text-sm mb-1">
                            Phone
                          </label>
                          <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            className="bg-gray-700 text-white rounded-md px-4 py-2 w-full"
                          />
                        </div>
                      </div>

                      <div className="mb-4">
                        <label htmlFor="company" className="block text-gray-400 text-sm mb-1">
                          Company/Organization *
                        </label>
                        <input
                          type="text"
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleInputChange}
                          required
                          className="bg-gray-700 text-white rounded-md px-4 py-2 w-full"
                        />
                      </div>

                      <div className="mb-4">
                        <label
                          htmlFor="sponsorshipType"
                          className="block text-gray-400 text-sm mb-1"
                        >
                          Sponsorship Interest
                        </label>
                        <select
                          id="sponsorshipType"
                          name="sponsorshipType"
                          value={formData.sponsorshipType}
                          onChange={handleInputChange}
                          className="bg-gray-700 text-white rounded-md px-4 py-2 w-full"
                        >
                          <option value="Interested in learning more">
                            Interested in learning more
                          </option>
                          <option value="Platinum Sponsor">Platinum Sponsor</option>
                          <option value="Gold Sponsor">Gold Sponsor</option>
                          <option value="Silver Sponsor">Silver Sponsor</option>
                          <option value="Bronze Sponsor">Bronze Sponsor</option>
                          <option value="Team Supporter">Team Supporter</option>
                        </select>
                      </div>

                      <div className="mb-6">
                        <label htmlFor="message" className="block text-gray-400 text-sm mb-1">
                          Message
                        </label>
                        <textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleInputChange}
                          rows={4}
                          className="bg-gray-700 text-white rounded-md px-4 py-2 w-full"
                          placeholder="Tell us about your company and your sponsorship interests..."
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className={`${
                          isSubmitting ? 'bg-gray-600' : 'bg-yellow-600 hover:bg-yellow-700'
                        } text-white px-6 py-3 rounded-md font-medium w-full flex items-center justify-center`}
                      >
                        {isSubmitting ? (
                          <>
                            <svg
                              className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            Submitting...
                          </>
                        ) : (
                          'Submit Sponsorship Inquiry'
                        )}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SponsorsPage;
